import { Box, Link, Stack, Tooltip } from '@mui/joy';
import { Repeat } from '@phosphor-icons/react';
import { ICellRendererParams } from 'ag-grid-community';
import { t } from 'i18next';

import { formatDate } from 'core/utils';
import { TradeAgreementUsage } from 'quotes/enums';
import { ITradeAgreement } from 'quotes/types';
import { isTradeAgreementExpired } from 'quotes/utils';
import { Color, Level } from 'theme/enums';

import { getTradeAgreementUsage, ITradeAgreementUsageContext } from '../../../utils';

interface ISpaCostCellParams
  extends ICellRendererParams<ITradeAgreement>,
    Partial<ITradeAgreementUsageContext> {
  onUse?: (agreement: ITradeAgreement) => void;
}

export const SpaCostCell = ({
  data,
  valueFormatted,
  defaultRowId,
  heldRowId,
  isDefaultInUse,
  onUse,
}: ISpaCostCellParams) => {
  if (!data) {
    return <>{valueFormatted}</>;
  }

  const usage = getTradeAgreementUsage(data, { defaultRowId, heldRowId, isDefaultInUse });
  const isExpired = isTradeAgreementExpired(data);
  const isInUse =
    TradeAgreementUsage.IN_USE === usage || TradeAgreementUsage.DEFAULT_IN_USE === usage;
  const isDefault =
    TradeAgreementUsage.DEFAULT === usage || TradeAgreementUsage.DEFAULT_IN_USE === usage;
  const disabled = isExpired || isInUse;
  const disabledReason = isExpired
    ? t('messageTradeAgreementExpired', { date: formatDate(data.spaCostEndDate) })
    : t('tooltipSpaAlreadyInUse');

  const onClick = () => {
    onUse?.(data);
  };

  const useLink = (
    <Link
      component="button"
      type="button"
      color={disabled ? Color.NEUTRAL : Color.PRIMARY}
      level={Level.BODY_XS}
      disabled={disabled}
      onClick={onClick}
      startDecorator={<Repeat />}
      // Joy keeps `disabled` for its own styling; the button element needs the attribute too.
      slotProps={{ root: { disabled } }}
      sx={{
        fontWeight: 600,
        ...(disabled && {
          cursor: 'default',
          opacity: 0.8,

          ':hover': {
            textDecoration: 'none',
          },
        }),
      }}
    >
      {isDefault ? t('buttonLabelUseDefaultSpa') : t('buttonLabelUseSpa')}
    </Link>
  );

  return (
    <Stack sx={{ alignItems: 'flex-end', gap: 0.5 }}>
      {valueFormatted}

      {onUse &&
        (disabled ? (
          // A disabled button fires no mouse events, so the tooltip listens on a wrapper.
          <Tooltip title={disabledReason} placement="top">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              {useLink}
            </Box>
          </Tooltip>
        ) : (
          useLink
        ))}
    </Stack>
  );
};
